import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { printerService } from '../services/printerService';

export const usePrinterData = (printerData, isEdit, token) => {
  const [products, setProducts] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [defaultWaiter, setDefaultWaiter] = useState(null);
  const [printerNames, setPrinterNames] = useState([]);
  const [printerNamesLoading, setPrinterNamesLoading] = useState(false);
  const [waitersLoading, setWaitersLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
  }, [printerData, isEdit]);

  const fetchPrinterNames = useCallback(async () => {
    if (isEdit) return;
    setPrinterNamesLoading(true);
    setError(null);
    try {
      const names = await printerService.getPrinterNames(token);
      setPrinterNames(names);
    } catch (err) {
      const errorMessage =
        err.message || 'Erro ao buscar nomes de impressoras.';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching printer names:', err);
    } finally {
      setPrinterNamesLoading(false);
    }
  }, [isEdit, token]);

  const fetchEditData = useCallback(async () => {
    if (!isEdit || !printerData?.id) return;
    setWaitersLoading(true);
    setProductsLoading(true);
    setError(null);
    try {
      const [productsData, waitersData] = await Promise.all([
        printerService.getProducts(printerData.id),
        printerService.getWaiters(),
      ]);
      setProducts(productsData);
      setWaiters(waitersData);
      let defaultWaiterData = null;
      if (waitersData.length > 0) {
        if (printerData.waiter_id) {
          defaultWaiterData = waitersData.find(
            (item) => item.value === printerData.waiter_id
          );
        }
        if (!defaultWaiterData) {
          [defaultWaiterData] = waitersData;
        }
      }
      setDefaultWaiter(defaultWaiterData);
    } catch (err) {
      const errorMessage = 'Erro ao carregar dados da impressora';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching edit data:', err);
    } finally {
      setWaitersLoading(false);
      setProductsLoading(false);
    }
  }, [isEdit, printerData]);

  const savePrinter = useCallback(
    async (printerPayload) => {
      setSaveLoading(true);
      setError(null);
      try {
        let response;
        if (isEdit && printerData?.id) {
          response = await printerService.updatePrinter(
            printerData.id,
            printerPayload
          );
        } else {
          response = await printerService.createPrinter(printerPayload);
        }
        return response;
      } catch (err) {
        const errorMessage = err.message || 'Erro ao salvar impressora';
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setSaveLoading(false);
      }
    },
    [isEdit, printerData]
  );

  useEffect(() => {
    fetchPrinterNames();
  }, [fetchPrinterNames]);

  useEffect(() => {
    fetchEditData();
  }, [fetchEditData]);

  return {
    products,
    waiters,
    defaultWaiter,
    printerNames,
    printerNamesLoading,
    waitersLoading,
    productsLoading,
    saveLoading,
    error,
    setDefaultWaiter,
    savePrinter,
    refetchPrinterNames: fetchPrinterNames,
    refetchEditData: fetchEditData,
  };
};
